import { type ExperienceMultiSurveyCardReasonSection } from '@bbkAdminRedux/rtkq/experience.slice';
import {
  SurveyType,
  type RestMultiRowSurveyCard,
} from '@bbkAdminUtils/api-client/rest-cards-types';
import { FeatureFlagsE } from '@bbkAdminUtils/feature-flags';

export default function isReasonOther(reasonKey: string): boolean {
  const reasonOtherRegex = /^(?:other_reason|other(\.\d+|_\d+)?)$/g;
  return reasonOtherRegex.test(reasonKey);
}

type ReasonOption =
  ExperienceMultiSurveyCardReasonSection['options'][number] & {
    show_text_input?: boolean;
  };

/** Runtime funnel payloads only include global flags; infer per-app flags from assembly. */
export const isMultiOtherInputEnabled = (items: ReasonOption[]): boolean => {
  if (
    window.bbkFeatureFlags?.includes(FeatureFlagsE.ENABLE_MULTI_OTHER_INPUT)
  ) {
    return true;
  }
  return items.some(
    (o) => o.show_text_input === true && !isReasonOther(o.value)
  );
};

/**
 * Whether a reason should *render* a free-text box. The Other reason always shows one; any other
 * reason shows one only when the multi-other capability is enabled and the assembled option carries
 * `show_text_input`. Distinct from {@link reasonRequiresTextInput}, which is about whether that text
 * is mandatory (only Other is).
 */
export const reasonShowsTextInput = (
  reasonValue: string,
  options: ReasonOption[],
  enableMultiOtherInput = true
): boolean => {
  if (isReasonOther(reasonValue)) {
    return true;
  }
  if (!enableMultiOtherInput) {
    return false;
  }
  const option = options.find((o) => o.value === reasonValue);
  return option?.show_text_input === true;
};

/** Only the legacy Other reason requires freeform text when the survey is required. */
export const reasonRequiresTextInput = (reasonValue: string): boolean =>
  isReasonOther(reasonValue);

export const findSelectedReason = (
  surveyType: SurveyType | '',
  response: Partial<RestMultiRowSurveyCard['provides']>,
  questions: RestMultiRowSurveyCard['requires']['multi_row_survey']
): { label: string } => {
  if (surveyType === SurveyType.multi_row_survey) {
    const lastReasonPicked = response.reason_for_leaving?.find(
      (reason) => reason.tier === 'tier0'
    );
    if (!lastReasonPicked) return { label: 'No reason given.' };
    const chosenQuestionGroup = questions.find(
      (q) => q.name === lastReasonPicked.name
    ) as ExperienceMultiSurveyCardReasonSection;
    const result = chosenQuestionGroup.options.find(
      (option) => option.value === lastReasonPicked.value
    );
    return result ?? { label: 'No reason given.' };
  }
  return { label: 'No reason given.' };
};
